import { useCallback } from "react";
import { convertirAMonedaLocal } from "../../../utils/calculateVentas";
import { calcCierresCartMotor, calcCierresHWMotor } from "../../../utils/carteleria/cierresProyeccionCarteleria";
import { combinarPorCorte, esMesHistorico } from "../../../utils/carteleria/corteHistorico";
import {
  calcCierresCartHistorico,
  calcCierresHWHistorico,
} from "../../../utils/carteleria/historico/proyeccionCierresHistorico";

export const useVPCierresCalcs = ({
  oportunidades,
  salesBTL,
  negociosCarteleria,
  contratosData,
  oportunidadesOtroPais,
  salesBTLOtroPais,
  negociosCarteleriaOtroPais,
  contratosDataOtroPais,
  pais,
}) => {
  // Cierres Publicidad genérico
  const calcCierresPub = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((o) => o.estatus === "100%" && !o.eliminada && o.mes_cierre)
      .forEach((o) => {
        const [mesStr, anoStr] = o.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = parseFloat(o.importe) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = o.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Cierres BTL genérico
  const calcCierresBTL = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((b) => b.status === 100 && !b.eliminada && b.mes_cierre)
      .forEach((b) => {
        const [mesStr, anoStr] = b.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = Number(b.total_investment) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = b.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Cierres Hardware genérico (100% y Activo = ya cerrados).
  // F3c: meses < 2026-10 → histórico congelado (idéntico a antes); meses >= 2026-10 → motor calendarioNegocio.
  const calcCierresHW = useCallback((data, paisParam, ano) => {
    const historico = calcCierresHWHistorico(data, paisParam, ano);
    if (esMesHistorico(ano, 11)) return historico; // el año entero es histórico
    return combinarPorCorte(ano, historico, calcCierresHWMotor(data, paisParam, ano));
  }, []);

  // Cierres Cartelería genérico (necesita país para conversión). F3c: igual que arriba (histórico + motor).
  const calcCierresCart = useCallback((data, paisParam, ano) => {
    const historico = calcCierresCartHistorico(data, paisParam, ano);
    if (esMesHistorico(ano, 11)) return historico;
    return combinarPorCorte(ano, historico, calcCierresCartMotor(data, paisParam, ano));
  }, []);

  // Cierres Contratos genérico (suma importe_total por mes_cierre)
  const calcCierresContratos = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => c.estatus === "100%" && !c.eliminada && c.mes_cierre)
      .forEach((c) => {
        const [mesStr, anoStr] = c.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = Number(c.importe_total) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = c.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Funciones expuestas (país actual)
  const calcularCierresPub = useCallback(
    (ano) => calcCierresPub(oportunidades, pais, ano),
    [oportunidades, pais, calcCierresPub]
  );
  const calcularCierresBTL = useCallback(
    (ano) => calcCierresBTL(salesBTL, pais, ano),
    [salesBTL, pais, calcCierresBTL]
  );
  const calcularCierresHW = useCallback(
    (ano) => calcCierresHW(negociosCarteleria, pais, ano),
    [negociosCarteleria, pais, calcCierresHW]
  );
  const calcularCierresCart = useCallback(
    (ano) => calcCierresCart(negociosCarteleria, pais, ano),
    [negociosCarteleria, pais, calcCierresCart]
  );

  const calcularCierresContratos = useCallback(
    (ano) => calcCierresContratos(contratosData, pais, ano),
    [contratosData, pais, calcCierresContratos]
  );

  // Funciones expuestas (otro país)
  const otroPais = pais === "Chile" ? "Perú" : "Chile";
  const calcularCierresPubOtro = useCallback(
    (ano) => calcCierresPub(oportunidadesOtroPais, otroPais, ano),
    [oportunidadesOtroPais, otroPais, calcCierresPub]
  );
  const calcularCierresBTLOtro = useCallback(
    (ano) => calcCierresBTL(salesBTLOtroPais, otroPais, ano),
    [salesBTLOtroPais, otroPais, calcCierresBTL]
  );
  const calcularCierresHWOtro = useCallback(
    (ano) => calcCierresHW(negociosCarteleriaOtroPais, otroPais, ano),
    [negociosCarteleriaOtroPais, otroPais, calcCierresHW]
  );
  const calcularCierresCartOtro = useCallback(
    (ano) => calcCierresCart(negociosCarteleriaOtroPais, otroPais, ano),
    [negociosCarteleriaOtroPais, otroPais, calcCierresCart]
  );

  const calcularCierresContratosOtro = useCallback(
    (ano) => calcCierresContratos(contratosDataOtroPais, otroPais, ano),
    [contratosDataOtroPais, otroPais, calcCierresContratos]
  );

  return {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  };
};
